import { ApolloClient, InMemoryCache, gql, useQuery } from '@apollo/client';
import { useState } from 'react';
import { Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import About from './About';
import Account from './Account';
import './App.css';
import Chat from './Chat';
import ErrorBoundary from './ErrorBoundary';
import Home from "./Home";
import Images from './Images';
import SignIn from './SignIn';
import SignInGetToken from "./SignInGetToken";
import SignInToken from "./SignInToken";
import Places from './Places';
import Purchase from './Purchase';
import Register from './Register';
import Report from "./Report";
import ReportEdit from './ReportEdit';
import Reports from './Reports';
import Travel from "./Travel";
import TravelDetails from "./TravelDetails";
import TravelEdit from './TravelEdit';
import Travels from './Travels';
import NotificationDisplay from './NotificationDisplay';
import UploadMedia from './UploadMedia';
import AboutPrivacy from './AboutPrivacy';
import AboutTermsOfService from './AboutTermsOfService';
import AppInfo from './AppInfo';
import { Avatar, Box, Button, Drawer, Fab, IconButton, Typography } from '@mui/material';
import Navbar from './Navbar';
import ChatIcon from '@mui/icons-material/Chat';
import GetActiveState from './GetActiveState';
import SendTelemetry from './SendTelemetry';
import Dashboard from './Dashboard';
import { useDrawer } from './context/DrawerContext';
import { Close } from '@mui/icons-material';

const ACCOUNT = gql`
query Account {
  account {
    id
    name
    purchase {
      status
    }
    roles
    pictureUrl
  }
}`;

const client = new ApolloClient({
  uri: process.env.REACT_APP_API_URI,
  // link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
  credentials: 'include',
  headers: {
    'x-api-key': process.env.REACT_APP_API_KEY
  }
});

const AccountButton = ({ account, client }) => {
    const { openDrawer } = useDrawer();

  const accountIsLoggedIn = !!account;

  const handleAvatarClick = () => {
    openDrawer('account')
  };

  if (!accountIsLoggedIn && window.location.pathname === '/') {
    return null;
  }

  return (
  <Box
      sx={{
        position: 'fixed',
        zIndex: 1000,
        top: 10,
        right: 10,
      }}
    >
      {accountIsLoggedIn ? (
        <IconButton
          onClick={handleAvatarClick}
          sx={{p: 0, m: 0, border: '1px solid #ccc'}}
        >
          <Avatar
            alt={account.name}
            src={account.pictureUrl}
          />
        </IconButton>
      ) : (
        <Button color="primary" onClick={() => openDrawer('sign-in')} variant="outlined" sx={{ fontWeight: 'bold' }}>
            Sign In
          </Button>
      )}
    </Box>)
}

const App = props => {
  const [searchParams] = useSearchParams();
  const { loading, data } = useQuery(ACCOUNT, { client: client });
  const account = data?.account;
  const [notifications, setNotifications] = useState([])
  const location = useLocation();
  const [activeState, setActiveState] = useState(null);
  const { drawerOpen, drawerSection, drawerOptions, closeDrawer } = useDrawer();

  const onError = e => setNotifications([...[e], ...notifications])

  console.log(`App version: ${process.env.REACT_APP_VERSION}`)

  AppInfo.setProfile(searchParams.get('app.profile'))

  if (loading) {
    return null
  }

  // A mapping of content strings to their corresponding components
  const contentMap = {
    'account': <Account account={account} client={client} />,
    'get-token' : <SignInGetToken client={client} />,
    'sign-up' : <Register account={account} client={client} />,
    'sign-in': <SignIn client={client} account={account} />,
    'sign-in-token': <SignInToken client={client} />,
    'drive': drawerOptions?.subject ? <Travel account={account} id={drawerOptions?.subject?.id} client={client} onError={onError} activeState={activeState} /> : <Travels client={client} />,
    'drive-new': <TravelEdit account={account} client={client} />,
    'chat': <Chat client={client} account={account} />,
    'forecast': <Places location={drawerOptions?.location} account={account} client={client} onError={onError} />,
    'default': (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">Select an option to view content. (selected={drawerSection})</Typography>
      </Box>
    ),
  };

  return (<ErrorBoundary>
    <div className='presentation'>
      <NotificationDisplay
        notifications={notifications}
        onClose={() => setNotifications([])}
      />
      <SendTelemetry account={account} client={client} />
      <GetActiveState client={client} onLoad={setActiveState} />
      <Navbar account={account} client={client} />
      <AccountButton account={account} client={client} />
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{
    sx: {
      width: '75%',
    },
  }}

      >
        <Box
    sx={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      pt: 0 
    }}
    role="presentation"
  >
 <Box sx={{ position: 'relative', width: '100%' }}>
  <IconButton
    onClick={closeDrawer}
    sx={{ position: 'absolute', top: 0, right: 20 }} // Adjust top and right values as needed
  >
    <Close />
  </IconButton>
  {/* ... rest of the content */}
</Box>

    <Box sx={{ flexGrow: 1, overflowY: 'auto', pt: 4 }}>
      {contentMap[drawerSection] || contentMap['default']}
    </Box>
  </Box>
      </Drawer>
      <div className='main' id='main'>
        <Routes>
          <Route path='/dashboard' element={<Dashboard client={client} activeState={activeState} account={account} />} />
          <Route path='/about' element={<About client={client} />} />
          <Route path='/about/privacy' element={<AboutPrivacy client={client} />} />
          <Route path='/about/terms-of-service' element={<AboutTermsOfService client={client} />} />
          <Route path='/account' element={<Account account={account} client={client} />} />
          <Route path='/account/purchase' element={<Purchase client={client} />} />
          <Route path='/chat' element={<Chat client={client} account={account} />} />
          <Route path='/local' element={<Places key={location.state?.key || 'default'} account={account} client={client} onError={onError} />} />
          <Route path='/record' element={<Reports account={account} client={client} />} />
          <Route path='/record/add' element={<ReportEdit client={client} account={account} />} />
          <Route path='/record/edit/:id' element={<ReportEdit client={client} />} />
          <Route path='/record/:id' element={<Report account={account} client={client} />} />
          <Route path='/drive' element={<Travels client={client} />} />
          <Route path='/drive/new' element={<TravelEdit account={account} client={client} />} />
          <Route path='/drive/edit/:id' element={<TravelEdit account={account} client={client} />} />
          <Route path='/drive/:id' element={<Travel account={account} client={client} onError={onError} activeState={activeState} />} />
          <Route path='/drive/:id/details' element={<TravelDetails client={client} />} />
          <Route path='/gallery' element={<Images account={account} client={client} />} />
          <Route path='/gallery/upload' element={<UploadMedia account={account} client={client} />} />
          <Route path='/sign-in' element={<SignIn client={client} account={account} />} />
          <Route path='/sign-in/get-token' element={<SignInGetToken client={client} />} />
          <Route path='/sign-in/token' element={<SignInToken client={client} />} />
          <Route path='/sign-up' element={<Register account={account} client={client} />} />
          <Route path='/' element={
            account ?
              <Dashboard client={client} activeState={activeState} account={account} /> :
              <Home account={account} client={client} activeState={activeState} />
          } />
        </Routes>
      </div>
      <NavButton account={account} />
    </div>
  </ErrorBoundary>)
}

const NavButton = props => {
  const { openDrawer } = useDrawer();
  return (
    <Fab
      color="primary"
      aria-label="chat"
      onClick={() => openDrawer('chat')}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
      }}
    >
      <ChatIcon />
    </Fab>
  )
}

export default App;
